import { useState, useCallback } from 'react';
import type { MessageDialogType } from '../components/MessageDialog';

interface DialogState {
  isOpen: boolean;
  type: MessageDialogType;
  title: string;
  message: string | React.ReactNode;
  onConfirm?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmClassName?: string;
}

const initialState: DialogState = {
  isOpen: false,
  type: 'info',
  title: '',
  message: '',
};

export function useMessageDialog() {
  const [dialogState, setDialogState] = useState<DialogState>(initialState);

  const closeDialog = useCallback(() => {
    setDialogState(initialState);
  }, []);

  const showAlert = useCallback((
    type: 'info' | 'success' | 'warning' | 'error',
    title: string,
    message: string | React.ReactNode
  ) => {
    setDialogState({
      isOpen: true,
      type,
      title,
      message,
    });
  }, []);

  const showConfirm = useCallback((
    title: string,
    message: string | React.ReactNode,
    onConfirm: () => void,
    options?: {
      confirmLabel?: string;
      cancelLabel?: string;
      confirmClassName?: string;
    }
  ) => {
    setDialogState({
      isOpen: true,
      type: 'confirm',
      title,
      message,
      onConfirm,
      confirmLabel: options?.confirmLabel,
      cancelLabel: options?.cancelLabel,
      confirmClassName: options?.confirmClassName,
    });
  }, []);

  return {
    dialogState,
    closeDialog,
    showAlert,
    showConfirm,
  };
}
